import React, { useMemo } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  APPEAL_TYPE_LABELS,
  AppealType,
  DCM_FUNNEL_VISUAL,
  DcmWho,
  FUNNEL_STAGE_OPTIONS,
  Product,
} from '../../types/knowledge';
import { productToDcmViewModel } from '../../utils/knowledgeDcmAdapter';

/**
 * 商品選択モーダルでセグメント／訴求軸を選ぶための、戦略マップ（/knowledge の StrategyMatrix）の
 * 読み取り専用・縮小版。
 *
 * 従来はセグメントと訴求軸が独立した <select> 2段で、ファネル段階もペルソナも見えず、
 * 「どのターゲットに何を訴えるか」の対応関係が読み取れなかった。ここでは戦略マップと同じ
 * 「ファネル × セグメント」のマス目を見せ、訴求カードのクリック1回で確定する。
 *
 * 追加・編集の導線は意図的に持たない（生成フローの途中で商品データを書き換えさせない）。
 */
export interface SegmentAppealSelection {
  segmentId: string;
  segmentName: string;
  appealId: string;
  appealName: string;
  appealDirection: string;
}

interface SegmentAppealPickerProps {
  product: Product | null;
  selectedAppealId: string | null;
  onSelect: (selection: SegmentAppealSelection) => void;
  onClear: () => void;
}

// 訴求タイプの色。StrategyMatrix と同じ配色を使い、戦略マップとの見た目の対応を保つ。
const APPEAL_TYPE_COLORS: Record<AppealType, string> = {
  functional: '#616FC9',
  value: '#44C2A4',
  trust: '#9675C9',
  feature: '#616FC9',
  merit: '#4F86C6',
  benefit: '#44C2A4',
  emotion: '#C9619C',
  negative: '#C9A061',
  offer: '#E0894B',
  comparison: '#5BA8C4',
  social: '#6FA86F',
};

// 商品登録直後のプレースホルダ（「セグメント1」「訴求2」等）は中身が空なので選ばせない。
// 旧UI（<select> 2段）も同じ条件で選択肢から除外していた挙動を引き継ぐ。
const isPlaceholderName = (name: string | undefined, prefix: string): boolean =>
  !name || name.trim() === '' || name.trim().startsWith(prefix);

const SegmentAppealPicker: React.FC<SegmentAppealPickerProps> = ({
  product,
  selectedAppealId,
  onSelect,
  onClear,
}) => {
  // adapter は types/product の Product を受けるが、本モーダル系は types/knowledge の Product を
  // 使っている。読むのは extended_metadata だけなので構造上は互換。
  const viewModel = useMemo(() => productToDcmViewModel(product as any), [product]);

  // 中身のあるセグメント／訴求だけを残す。訴求が1つも残らないペルソナは行ごと、
  // 選べる訴求を持たないセグメントは列ごと出さない（幅の限られたモーダルで
  // 選べない列が場所を取らないように）。
  const namedSegments = useMemo(
    () => viewModel.segmentMaster.filter((segment) => !isPlaceholderName(segment.segment_name, 'セグメント')),
    [viewModel.segmentMaster]
  );

  const selectableWho = useMemo(() => {
    const segmentIds = new Set(namedSegments.map((segment) => segment.segment_id));
    return viewModel.who
      .filter((item) => segmentIds.has(item.segment_id))
      .map((item) => ({
        ...item,
        appeals: item.appeals.filter((appeal) => !isPlaceholderName(appeal.name, '訴求')),
      }))
      .filter((item) => item.appeals.length > 0);
  }, [viewModel.who, namedSegments]);

  const segments = useMemo(() => {
    const usedSegmentIds = new Set(selectableWho.map((item) => item.segment_id));
    return namedSegments.filter((segment) => usedSegmentIds.has(segment.segment_id));
  }, [namedSegments, selectableWho]);

  const whoByCell = useMemo(() => {
    const map = new Map<string, DcmWho[]>();
    selectableWho.forEach((item) => {
      const key = `${item.segment_id}:${item.funnel_stage_id}`;
      map.set(key, [...(map.get(key) || []), item]);
    });
    return map;
  }, [selectableWho]);

  const segmentNameById = useMemo(
    () => new Map(segments.map((segment) => [segment.segment_id, segment.segment_name])),
    [segments]
  );

  if (selectableWho.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-center">
        <p className="text-[12px] leading-relaxed text-gray-500">
          この商品にはまだ対象者・伝えたい魅力が登録されていません。
          <br />
          「商品情報」の戦略マップから追加できます。
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl bg-[#f8f7f3] p-2">
      <p className="mb-2 px-1 text-xs leading-relaxed text-gray-600">相手がどのくらい検討しているかを選び、伝えたい魅力のカードを押してください。例：「比べている」人に、選ぶ理由を伝えます。</p>
      {selectedAppealId && (
        <div className="mb-1.5 flex justify-end">
          <button
            type="button"
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-gray-500 transition-colors duration-150 hover:bg-white hover:text-gray-700"
            onClick={onClear}
          >
            <RotateCcw size={12} />
            選択を解除
          </button>
        </div>
      )}

      {/* 高さは画面に追従させる。大きい画面ではマトリクス全体（4ファネル行）が
          一望でき内部スクロールが消え、小さい画面では内部スクロール＋sticky
          ヘッダで列の意味を保つ。 */}
      <div className="max-h-[min(52vh,560px)] overflow-auto pb-1">
        <div
          className="grid gap-1.5"
          style={{
            gridTemplateColumns: `92px repeat(${segments.length}, minmax(168px, 1fr))`,
          }}
        >
          <div className="sticky left-0 top-0 z-[8] flex items-center rounded-[10px] bg-[#f8f7f3] py-1.5 pl-2 pr-1 shadow-[6px_0_0_0_#f8f7f3]">
            <span className="text-[10px] font-semibold leading-snug text-gray-400">検討段階 ＼ 誰向け</span>
          </div>
          {segments.map((segment) => (
            <div
              key={segment.segment_id}
              // 定義文は title に逃がし、ヘッダは名前1行に保つ。行の背が高いと
              // モーダル側のスクロールで sticky ごと流れたときに名前が切れる。
              title={segment.definition ? `${segment.segment_name}｜${segment.definition}` : segment.segment_name}
              className="sticky top-0 z-[7] rounded-[10px] border border-[#dcd6c9] bg-white px-2 py-1.5 text-center"
            >
              <span className="line-clamp-2 block text-[12px] font-bold leading-snug text-dark">
                {segment.segment_name}
              </span>
            </div>
          ))}

          {FUNNEL_STAGE_OPTIONS.map((stage) => {
            const visual = DCM_FUNNEL_VISUAL[stage.value];
            return (
              <React.Fragment key={stage.value}>
                <div
                  className="sticky left-0 z-[6] flex items-center gap-1.5 rounded-[10px] py-1.5 pl-2 pr-1 shadow-[6px_0_0_0_#f8f7f3]"
                  style={{ background: visual.labelBg }}
                >
                  <span className="material-icons-round text-[15px]" style={{ color: visual.iconColor }}>
                    {visual.icon}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold leading-tight" style={{ color: visual.labelColor }}>
                      {stage.sub}
                    </div>
                    <div className="mt-1 text-[10px] text-gray-500">{stage.label}</div>
                  </div>
                </div>
                {segments.map((segment) => {
                  const cellWho = whoByCell.get(`${segment.segment_id}:${stage.value}`) || [];
                  if (cellWho.length === 0) {
                    // 読み取り専用なので空セルには追加導線を出さず、枠だけ残して構造を保つ。
                    return (
                      <div
                        key={`${segment.segment_id}-${stage.value}`}
                        className="min-h-[44px] rounded-xl border border-dashed border-[#e4e0d6]"
                        aria-hidden="true"
                      />
                    );
                  }
                  return (
                    <div
                      key={`${segment.segment_id}-${stage.value}`}
                      className="flex min-h-[44px] flex-col gap-1 rounded-xl border border-[#dcd6c9] bg-white p-1.5"
                    >
                      {cellWho.map((item) => (
                        <div key={item.who_id}>
                          <div className="flex items-center gap-1 px-0.5 py-0.5">
                            <span className="material-icons-round text-[13px] text-gray-400">person</span>
                            <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[10px] font-bold leading-tight text-gray-600">
                              {item.who_name}
                            </span>
                          </div>
                          {item.appeals.map((appeal) => {
                            const isSelected = appeal.appeal_id === selectedAppealId;
                            return (
                              <button
                                key={appeal.appeal_id}
                                type="button"
                                aria-pressed={isSelected}
                                title={`訴求タイプ: ${APPEAL_TYPE_LABELS[appeal.type] || appeal.type} ／ 訴求軸: ${appeal.appeal_axis || '-'}`}
                                className={`mt-1 flex w-full cursor-pointer items-center gap-1.5 rounded-[7px] border px-1.5 py-1 text-left transition-[border-color,box-shadow,transform] duration-[120ms] hover:-translate-y-px hover:border-[#aeb5de] hover:shadow-[0_3px_10px_rgba(97,111,201,0.13)] ${
                                  isSelected
                                    ? 'border-primary bg-[#eef0fa] shadow-[inset_0_0_0_1px_#616fc9]'
                                    : 'border-warm-border bg-white'
                                }`}
                                onClick={() =>
                                  onSelect({
                                    segmentId: item.who_id,
                                    segmentName: segmentNameById.get(item.segment_id) || item.who_name,
                                    appealId: appeal.appeal_id,
                                    appealName: appeal.name,
                                    appealDirection: appeal.direction || '',
                                  })
                                }
                              >
                                <span
                                  className="h-[13px] w-[3px] flex-shrink-0 rounded-sm"
                                  style={{ backgroundColor: APPEAL_TYPE_COLORS[appeal.type] || '#616FC9' }}
                                />
                                <span className="flex min-w-0 flex-1 flex-col gap-px">
                                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[9px] font-bold leading-[1.15] text-[#a87c2b]">
                                    {appeal.appeal_axis || '訴求軸未設定'}
                                  </span>
                                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-semibold leading-[1.2] text-dark">
                                    {appeal.name}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SegmentAppealPicker;
